import { ChangeEvent, ReactElement, useEffect, useRef } from "react";
import { Box, Button, Checkbox, darken, FormControlLabel, Grid2, Menu, MenuItem, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { useState } from "react";
import { PageContainer } from "../../HelpPage/UI/PageContainer";
import { CardContainer } from "../../Common/UI/components/CardContainer";
import useFetchMsal from "../../Common/UI/hooks/useFetchMsal";
import { config } from "../../authConfig";
import { LoadingIndicator } from "../../Common/UI/components/Loading";
import { theme } from "../../Common/UI/styles/theme";
import styles from "./styles/Reports.module.css";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { GridLoadingOverlay } from "../../Common/UI/components/GridLoadingOverlay";
import DownloadIcon from "@mui/icons-material/Download";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";

interface ReportsData {
  paymentAmount: number;
  taxAmount: number;
  tipAmount: number;
  checkNumber: string | null;
  storeLocation: string;
  recordType: 'Detail' | 'Total';
}

function ReportPage(): ReactElement {
  const execute = useFetchMsal();
  const gridRef = useRef<AgGridReact>(null);
  const [searchDate, setSearchDate] = useState<Dayjs | null>(dayjs().add(-1, "day"));
  const [dateError, setDateError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [generalError, setGeneralError] = useState<string>("");
  const [reportsData, setReportsData] = useState<ReportsData[]>([]);
  const [menuAnchorElement, setMenuAnchorElement] = useState<HTMLElement | null>(null);
  const [colsDef, setColsDef] = useState<ColDef[]>([
    { field: "paymentAmount", headerName: "Payment Amount" },
    { field: "taxAmount", headerName: "Tax Amount" },
    { field: "tipAmount", headerName: "Tip Amount" },
    { field: "checkNumber", headerName: "Check Number" },
    { field: "storeLocation", headerName: "Store", filter: "agTextColumnFilter", filterParams: { maxNumConditions: 4 } },
  ]);

  const searchReports = () => {
    if (searchDate) {
      const date = searchDate.format("YYYY-MM-DD");
      setGeneralError("");

      const url = config?.rootAPIList?.reports.replace("{date}", date);
      setLoading(true);
      execute("GET", `${config.rootAPIUrl}${url}`)
        .then((result) => {
          const data: ReportsData[] = result.report;
          if (data.length <= 0) {
            setGeneralError("Report for this date not available!");
          } else {
            // const totalRow = {
            //   paymentAmount: `$${data.filter(row => row.recordType !== 'Total').reduce((sum, row) => sum + row.paymentAmount, 0).toFixed(2)}`,
            //   taxAmount: `$${data.filter(row => row.recordType !== 'Total').reduce((sum, row) => sum + row.taxAmount, 0).toFixed(2)}`,
            //   tipAmount: `$${data.filter(row => row.recordType !== 'Total').reduce((sum, row) => sum + row.tipAmount, 0).toFixed(2)}`,
            //   checkNumber: "",
            //   storeLocation: "",
            // };
            // setTotalRow([totalRow]);

            const mappedData = data.map((item: ReportsData) => {
              if (item.recordType === 'Total') {
                item.storeLocation = `${item.storeLocation} - Total`;
              }
              return item;
            })
            setReportsData(mappedData);
          }
        })
        .catch((error) => {
          setGeneralError("Report for this date not available!");
          console.log(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // fix having to scroll down every search on smaller screen
  useEffect(() => {
    window.scrollTo(0, 500);
  }, [reportsData])

  const handleExportAsCsvButtonClick = () => {
    gridRef.current?.api.exportDataAsCsv({
      fileName: `export_${dayjs(dayjs()).format("YYYY_MM_DD_HH_mm_ss").toString()}`,
    });
  };

  const handleColumnsMenuChange = (e: ChangeEvent<HTMLInputElement>) => {
    setColsDef((prev) => prev.map((col: ColDef) => (col.field === e.target.name ? { ...col, hide: !col.hide } : col)));
  };

  const formatReportsWithDollarSigns = (data: ReportsData[]) => {
    return data.map((report) => ({
      ...report,
      paymentAmount: `$${report.paymentAmount.toFixed(2)}`,
      taxAmount: `$${report.taxAmount.toFixed(2)}`,
      tipAmount: `$${report.tipAmount.toFixed(2)}`,
    }));
  };

  return (
    <PageContainer>
      <CardContainer title="Reports" className="reports">
        <Grid2 container sx={{ maxWidth: "800px" }}>
          <Grid2 size={5} sx={{ pr: "10px", marginTop: "20px" }}>
            <DatePicker
              sx={{ width: "100%" }}
              onError={(newError) => {
                if (newError !== null) {
                  setDateError(true);
                }
              }}
              label={"Business Date"}
              value={searchDate}
              onChange={(newValue) => {
                setSearchDate(newValue);
                setDateError(false);
              }}
              disableFuture
            />
          </Grid2>
          <Grid2 size={2} sx={{ textAlign: "center", marginTop: "20px" }}>
            <Button onClick={searchReports} disabled={dateError || loading} style={{ width: "120px" }}>
              Load
            </Button>
          </Grid2>
        </Grid2>
        <Box>
          <Typography color={theme.palette.error.main}>{generalError}</Typography>
        </Box>
        <div className="spacer-small"></div>

        <LoadingIndicator show={loading}>
          {reportsData.length > 0 ? (
            <>
              <div>
                <Box sx={{ display: "flex", mb: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<ViewWeekIcon />}
                    sx={{
                      mb: 1,
                      mr: 1,
                      backgroundColor: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: darken(theme.palette.primary.main, 0.1),
                      },
                    }}
                    disableElevation
                    onClick={(e) => setMenuAnchorElement(e.currentTarget)}
                  >
                    Columns
                  </Button>
                  <Button
                    sx={{
                      mb: 1,
                      backgroundColor: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: darken(theme.palette.primary.main, 0.1),
                      },
                    }}
                    startIcon={<DownloadIcon />}
                    onClick={handleExportAsCsvButtonClick}
                  >
                    Export as CSV
                  </Button>
                  <Menu open={Boolean(menuAnchorElement)} anchorEl={menuAnchorElement} onClose={() => setMenuAnchorElement(null)}>
                    {colsDef.map((col: ColDef) => (
                      <MenuItem key={col.field}>
                        <FormControlLabel control={<Checkbox checked={!col.hide} name={col.field} onChange={handleColumnsMenuChange} />} label={col.headerName} />
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </div>
              <div id={styles.eventsGrid} className="ag-theme-quartz">
                <AgGridReact
                  columnDefs={colsDef}
                  rowData={formatReportsWithDollarSigns(reportsData) || []}
                  loading={loading}
                  loadingOverlayComponent={GridLoadingOverlay}
                  ref={gridRef}
                  pagination
                  paginationPageSize={20}
                  defaultColDef={{
                    headerClass: "tableHeader",
                    cellStyle: (params) => {
                      if (params.node.rowPinned) {
                        return { fontWeight: "bold" };
                      }
                      return undefined;
                    },
                  }}
                  // pinnedBottomRowData={totalRow}
                  getRowStyle={(params) => {
                    if (params.node.rowPinned || params.data?.recordType === 'Total') {
                      return {
                        fontWeight: 'bold',
                        backgroundColor: "rgba(255, 193, 7, 0.12)"
                      };
                    }
                    return undefined;
                  }}
                  onGridReady={() => gridRef.current?.api.sizeColumnsToFit()}
                />
              </div>
            </>
          ) : (
            <></>
          )}
        </LoadingIndicator>
      </CardContainer>
    </PageContainer>
  );
}
export default ReportPage;
